import { useState, useEffect, useMemo } from 'react';
import { Card, Loader, Btn } from './UI';
import {
  loadStructure, loadRegistry, reconcile, groupStats, pctOf,
  listSnapshots, loadSnapshotRows, applyDynamics, saveSnapshot, exportReferralExcel,
} from '../lib/referral';

// Короны для топ-3: золотая, серебряная, бронзовая
function Crown({ place }) {
  const box = { width: 24, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' };
  if (place === 1) return (
    <span style={box}><svg width="22" height="20" viewBox="0 0 24 22" role="img" aria-label="1 место">
      <path d="M2 7l5 4 5-8 5 8 5-4-2 12H4z" fill="#E8B923" stroke="#B88A00" strokeWidth="1.2" strokeLinejoin="round" />
      <circle cx="2" cy="7" r="1.6" fill="#E8B923" /><circle cx="12" cy="3" r="1.6" fill="#E8B923" /><circle cx="22" cy="7" r="1.6" fill="#E8B923" />
      <rect x="4" y="19" width="16" height="2" rx="1" fill="#B88A00" /></svg></span>);
  if (place === 2) return (
    <span style={box}><svg width="20" height="18" viewBox="0 0 24 22" role="img" aria-label="2 место">
      <path d="M3 8l4.5 3.5L12 5l4.5 6.5L21 8l-2 11H5z" fill="#C3CCD8" stroke="#7D8896" strokeWidth="1.2" strokeLinejoin="round" /></svg></span>);
  if (place === 3) return (
    <span style={box}><svg width="18" height="16" viewBox="0 0 24 22" role="img" aria-label="3 место">
      <path d="M3 11l5 3 4-8 4 8 5-3-2 8H5z" fill="#CD8A4B" stroke="#8A5220" strokeWidth="1.2" strokeLinejoin="round" /></svg></span>);
  return <span style={box} />;
}

function Kpi({ label, value, color, sub }) {
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', flex: '1 1 130px', minWidth: 120 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || 'var(--text)', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  );
}

function RankRow({ place, name, pct, conn, total, mine, onClick, hideCount }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 8, cursor: onClick ? 'pointer' : 'default',
      background: mine ? '#E8F1FA' : 'transparent',
    }}>
      <span style={{ width: 18, fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textAlign: 'right' }}>{place}</span>
      <Crown place={place} />
      <span style={{ width: 130, fontSize: 13, fontWeight: mine ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      <div style={{ flex: 1, height: 12, background: '#E3E8F1', borderRadius: 6, minWidth: 40 }}>
        <div style={{ height: 12, borderRadius: 6, width: pct + '%', background: mine ? '#2E75B6' : '#9FB6D1' }} />
      </div>
      <span style={{ width: 78, textAlign: 'right', fontSize: 13, fontWeight: 600 }}>
        {pct}%{!hideCount && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {conn}/{total}</span>}
      </span>
    </div>
  );
}

function MrcTable({ rows, showSv, svName, dyn }) {
  const [q, setQ] = useState('');
  const [f, setF] = useState('all');
  const shown = rows.filter(m =>
    (f === 'all' || (f === 'yes' ? m.connected : !m.connected)) &&
    (!q || m.name.toLowerCase().includes(q.toLowerCase())));
  const pill = (on, label, k) => (
    <button key={k} onClick={() => setF(k)} style={{
      padding: '5px 10px', fontSize: 12, borderRadius: 8, border: 'none', cursor: 'pointer',
      background: on ? 'white' : 'transparent', fontWeight: on ? 600 : 400, boxShadow: on ? '0 1px 3px #0002' : 'none',
    }}>{label}</button>);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Поиск по ФИО"
          style={{ flex: '1 1 160px', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} />
        <div style={{ display: 'flex', background: '#EEF1F7', borderRadius: 8, padding: 2 }}>
          {pill(f === 'all', 'Все', 'all')}{pill(f === 'yes', 'Подключён', 'yes')}{pill(f === 'no', 'Нет', 'no')}
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 11 }}>
            <th style={{ padding: '6px 6px' }}>Фамилия и имя</th>
            {showSv && <th style={{ padding: '6px 6px' }}>СВ</th>}
            <th style={{ padding: '6px 6px' }}>Телефон</th>
            <th style={{ padding: '6px 6px' }}>Статус</th>
            <th style={{ padding: '6px 6px' }}>Совпадение по</th>
            {dyn && <th style={{ padding: '6px 6px' }}>Изменение</th>}
          </tr></thead>
          <tbody>
            {shown.map((m, i) => (
              <tr key={m.route_code + i} style={{ borderTop: '1px solid #EEF1F7' }}>
                <td style={{ padding: '7px 6px', fontWeight: 500 }}>{m.name}</td>
                {showSv && <td style={{ padding: '7px 6px', color: 'var(--text-muted)' }}>{svName(m.sv_id)}</td>}
                <td style={{ padding: '7px 6px', color: 'var(--text-muted)' }}>{m.phone}</td>
                <td style={{ padding: '7px 6px' }}>
                  <span style={{ padding: '2px 9px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                    background: m.connected ? '#EAF6EF' : '#FDECEA', color: m.connected ? '#1F7A44' : '#B3261E' }}>
                    {m.connected ? 'Подключён' : 'Не подключён'}</span>
                </td>
                <td style={{ padding: '7px 6px', color: 'var(--text-muted)' }}>{m.by || '—'}</td>
                {dyn && <td style={{ padding: '7px 6px', fontWeight: 600, color: m.change === 'new' ? '#1F7A44' : '#B3261E' }}>
                  {m.change === 'new' ? 'Новое подключение' : m.change === 'off' ? 'Отключился' : ''}</td>}
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={6} style={{ padding: 14, color: 'var(--text-muted)' }}>Никого нет</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Показано {shown.length} из {rows.length}</div>
    </div>
  );
}

// mode: 'admin' | 'rm' | 'tm' | 'sv'
export default function ReferralBoard({ mode, userId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [structure, setStructure] = useState(null);
  const [base, setBase] = useState([]);          // сверка без динамики
  const [regCount, setRegCount] = useState(0);
  const [snaps, setSnaps] = useState([]);
  const [snapId, setSnapId] = useState('');
  const [snapRows, setSnapRows] = useState(null);
  const [selTm, setSelTm] = useState(null);
  const [selSv, setSelSv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const wide = mode === 'admin' || mode === 'rm';

  async function load() {
    setLoading(true); setError('');
    try {
      const [st, reg, sn] = await Promise.all([loadStructure(), loadRegistry(), wide ? listSnapshots() : Promise.resolve([])]);
      setStructure(st); setRegCount(reg.length); setBase(reconcile(st, reg)); setSnaps(sn);
      if (sn.length && !snapId) setSnapId(sn[0].id);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!wide || !snapId) { setSnapRows(null); return; }
    loadSnapshotRows(snapId).then(setSnapRows).catch(() => setSnapRows(null));
  }, [snapId, wide]);

  const dynRes = useMemo(() => (snapRows ? applyDynamics(base, snapRows) : null), [base, snapRows]);
  const list = dynRes ? dynRes.list : base;

  if (loading) return <Loader />;
  if (error) return <div style={{ color: 'var(--red)', fontSize: 13 }}>Ошибка: {error}. Если таблицы ещё не созданы — примените SQL v107.</div>;

  const tmName = (id) => (structure.tms.find(t => t.id === id) || {}).name || 'Без ТМ';
  const svById = new Map(structure.svs.map(s => [s.id, s]));
  const svName = (id) => (svById.get(id) || {}).name || '—';
  const total = list.length, conn = list.filter(m => m.connected).length;

  const kpis = (rows) => {
    const c = rows.filter(m => m.connected).length;
    return (
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <Kpi label="Сотрудников" value={rows.length} />
        <Kpi label="Подключено" value={c} color="#1F7A44" />
        <Kpi label="Доля подключения" value={pctOf(c, rows.length) + '%'} />
        <Kpi label="Не подключено" value={rows.length - c} color="#B3261E" />
      </div>);
  };

  const crumbs = (items) => (
    <div style={{ fontSize: 13, marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {items.map((it, i) => (
        <span key={i} onClick={it.onClick} style={{ color: it.onClick ? 'var(--accent)' : 'var(--text)', cursor: it.onClick ? 'pointer' : 'default', fontWeight: it.onClick ? 500 : 700 }}>
          {i > 0 && <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>›</span>}{it.label}</span>))}
    </div>);

  async function doSnapshot() {
    setBusy(true); setNote('');
    try { const s = await saveSnapshot(base); setNote('Снимок сохранён'); const sn = await listSnapshots(); setSnaps(sn); setSnapId(s.id); }
    catch (e) { setNote('Ошибка: ' + (e.message || e)); } finally { setBusy(false); }
  }

  const tmRank = groupStats(list, m => m.tm_id || '—');
  const fmtDate = (s) => new Date(s).toLocaleDateString('ru-RU');

  const toolbar = wide && (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Сравнить со снимком:{' '}
        <select value={snapId} onChange={e => setSnapId(e.target.value)} style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="">— без сравнения —</option>
          {snaps.map(s => <option key={s.id} value={s.id}>{fmtDate(s.taken_at)} · {s.connected}/{s.total}</option>)}
        </select></label>
      <div style={{ flex: 1 }} />
      {mode === 'admin' && <Btn size="sm" color="ghost" onClick={doSnapshot} disabled={busy}>{busy ? 'Сохраняю…' : 'Сохранить снимок'}</Btn>}
      <Btn size="sm" onClick={() => exportReferralExcel({ list, structure, dyn: dynRes })}>⬇ Excel</Btn>
      {note && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{note}</span>}
    </div>);

  const dynBlock = dynRes && (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
      <Kpi label="Было → стало" value={`${dynRes.was} → ${dynRes.now}`} />
      <Kpi label="Новые подключения" value={'+' + dynRes.added} color="#1F7A44" />
      <Kpi label="Отключились" value={'−' + dynRes.off} color="#B3261E" />
      <Kpi label="Выбыли из табеля" value={dynRes.left} />
    </div>);

  // ── Админ / РМ ───────────────────────────────────────────────────────────
  if (wide) {
    if (selTm && selSv) {
      const rows = list.filter(m => m.sv_id === selSv);
      return (<div>{crumbs([{ label: 'Все ТМ', onClick: () => { setSelTm(null); setSelSv(null); } }, { label: tmName(selTm), onClick: () => setSelSv(null) }, { label: svName(selSv) }])}
        {kpis(rows)}<Card><div style={{ padding: 14 }}><MrcTable rows={rows} dyn={!!dynRes} /></div></Card></div>);
    }
    if (selTm) {
      const rows = list.filter(m => m.tm_id === selTm);
      const sr = groupStats(rows, m => m.sv_id || '—');
      return (<div>{crumbs([{ label: 'Все ТМ', onClick: () => setSelTm(null) }, { label: tmName(selTm) }])}
        {kpis(rows)}
        <Card><div style={{ padding: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>СВ команды</div>
          {sr.map((g, i) => <RankRow key={g.key} place={i + 1} name={svName(g.key)} pct={g.pct} conn={g.conn} total={g.total} onClick={() => setSelSv(g.key)} />)}
        </div></Card></div>);
    }
    return (<div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>В реестре подключённых: {regCount}</div>
      {toolbar}{kpis(list)}{dynBlock}
      <Card><div style={{ padding: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Рейтинг ТМ</div>
        {tmRank.map((g, i) => <RankRow key={g.key} place={i + 1} name={tmName(g.key)} pct={g.pct} conn={g.conn} total={g.total} onClick={() => setSelTm(g.key)} />)}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Нажмите на ТМ, чтобы спуститься до СВ и мерчендайзеров.</div>
      </div></Card></div>);
  }

  // ── ТМ ───────────────────────────────────────────────────────────────────
  if (mode === 'tm') {
    const mine = list.filter(m => m.tm_id === userId);
    const place = tmRank.findIndex(g => g.key === userId) + 1;
    if (selSv) {
      const rows = mine.filter(m => m.sv_id === selSv);
      return (<div>{crumbs([{ label: 'Моя команда', onClick: () => setSelSv(null) }, { label: svName(selSv) }])}
        {kpis(rows)}<Card><div style={{ padding: 14 }}><MrcTable rows={rows} /></div></Card></div>);
    }
    const sr = groupStats(mine, m => m.sv_id || '—');
    return (<div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <Kpi label="Сотрудников" value={mine.length} />
        <Kpi label="Подключено" value={mine.filter(m => m.connected).length} color="#1F7A44" />
        <Kpi label="Доля подключения" value={pctOf(mine.filter(m => m.connected).length, mine.length) + '%'} />
        <Kpi label="Моё место среди ТМ" value={place ? `${place} из ${tmRank.length}` : '—'} />
      </div>
      <Card><div style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Рейтинг ТМ проекта</div>
        {tmRank.map((g, i) => <RankRow key={g.key} place={i + 1} name={g.key === userId ? tmName(g.key) + ' (я)' : tmName(g.key)} pct={g.pct} mine={g.key === userId} hideCount />)}
      </div></Card>
      <div style={{ height: 12 }} />
      <Card><div style={{ padding: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Мои СВ</div>
        {sr.map((g, i) => <RankRow key={g.key} place={i + 1} name={svName(g.key)} pct={g.pct} conn={g.conn} total={g.total} onClick={() => setSelSv(g.key)} />)}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Нажмите на СВ, чтобы увидеть мерчендайзеров и статус.</div>
      </div></Card></div>);
  }

  // ── СВ ───────────────────────────────────────────────────────────────────
  const mySvs = structure.svs.filter(s => s.user_id === userId);
  const mySvIds = new Set(mySvs.map(s => s.id));
  const mine = list.filter(m => mySvIds.has(m.sv_id));
  const main = groupStats(mine, m => m.sv_id)[0];
  const mainSv = main ? svById.get(main.key) : null;
  const teamRows = mainSv ? list.filter(m => m.tm_id === mainSv.tm_id) : [];
  const svRank = groupStats(teamRows, m => m.sv_id || '—');
  const place = svRank.findIndex(g => mySvIds.has(g.key)) + 1;
  return (<div>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
      <Kpi label="Мерчендайзеров" value={mine.length} />
      <Kpi label="Подключено" value={mine.filter(m => m.connected).length} color="#1F7A44" />
      <Kpi label="Доля подключения" value={pctOf(mine.filter(m => m.connected).length, mine.length) + '%'} />
      <Kpi label="Моё место среди СВ" value={place ? `${place} из ${svRank.length}` : '—'} />
    </div>
    <Card><div style={{ padding: 14 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Рейтинг СВ в команде ТМ</div>
      {svRank.map((g, i) => <RankRow key={g.key} place={i + 1} name={mySvIds.has(g.key) ? svName(g.key) + ' (я)' : svName(g.key)} pct={g.pct} mine={mySvIds.has(g.key)} hideCount />)}
    </div></Card>
    <div style={{ height: 12 }} />
    <Card><div style={{ padding: 14 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Мои мерчендайзеры</div>
      <MrcTable rows={mine} />
    </div></Card>
  </div>);
}
